// Compare.jsx — UI matching reference design (dashboard + ledger)
import React, { useEffect, useMemo, useRef, useState } from "react";
import API from "../api/compare";
import axios from "axios";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";
import { Calendar, LayoutGrid, Filter, Download, FileUp, Clock } from "lucide-react";
import "./Compare.css";

const PLATFORMS = ["Flipkart", "Amazon", "Meesho"];

// Mock data matching image design
const MOCK_SALES_TREND = [
  { week: "WEEK 01", value: 180, revenue: 210000 },
  { week: "WEEK 02", value: 220, revenue: 208000 },
  { week: "WEEK 03", value: 195, revenue: 215000 },
  { week: "WEEK 04", value: 247, revenue: 209000 },
];

const MOCK_DAILY_ORDERS = [
  { day: "MON", orders: 38 },
  { day: "TUE", orders: 42 },
  { day: "WED", orders: 35 },
  { day: "THU", orders: 45 },
  { day: "FRI", orders: 52 },
  { day: "SAT", orders: 48 },
  { day: "SUN", orders: 40 },
];

const MOCK_SKU_LEDGER = [
  { skuId: "SKU-2024-FL-001", product: "Cotton Polo Shirt - Navy Blue (M)", platform: "Flipkart", qtySold: 150, qtyShipped: 148, status: "SHORT SHIPPED (-2)", statusType: "short" },
  { skuId: "SKU-2024-AM-002", product: "Wireless Earbuds - Black", platform: "Amazon", qtySold: 85, qtyShipped: 85, status: "MATCHED", statusType: "matched" },
  { skuId: "SKU-2024-MS-003", product: "Canvas Shoes - White", platform: "Meesho", qtySold: 210, qtyShipped: 210, status: "MATCHED", statusType: "matched" },
];

const MOCK_PAYMENT_LEDGER = [
  { orderId: "ORD-FK-88291", sku: "SKU-2024-FL-001", expected: 1240, received: 1120, difference: -120 },
  { orderId: "ORD-AM-77302", sku: "SKU-2024-AM-002", expected: 850, received: 850, difference: 0 },
  { orderId: "ORD-FK-88295", sku: "SKU-2024-FL-005", expected: 2100, received: 1900, difference: -200 },
  { orderId: "ORD-MS-55401", sku: "SKU-2024-MS-003", expected: 1890, received: 1890, difference: 0 },
];

const MOCK_RATE_CARD = [
  { platform: "Flipkart", label: "FLIPKART COMMISSION", rate: "12%", charged: "14%", conflict: true },
  { platform: "Amazon", label: "AMAZON REFERRAL FEE", rate: "8.5%", charged: "8.5%", conflict: false },
];

const MOCK_AUDIT = [
  { time: "14:22:10", before: "Master Rate Card updated by ", link: "Admin" },
  { time: "12:15:45", before: "", highlight: "45 Payment mismatches", after: " detected in Flipkart settlement" },
  { time: "10:05:12", before: "Export generated: ", link: "ledger_sept_2023.csv" },
];

export default function CompareDashboard() {
  const [dateRange, setDateRange] = useState("30d");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [overview, setOverview] = useState(null);
  const [skuComparisons, setSkuComparisons] = useState([]);
  const [paymentMismatches, setPaymentMismatches] = useState([]);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = { period: dateRange, platform: platformFilter };
        const [ov, sku] = await Promise.all([
          API.get("/overview", { params }).catch(() => ({ data: null })),
          API.get("/sku-comparison", { params }).catch(() => ({ data: [] })),
        ]);
        setOverview(ov?.data ?? null);
        setSkuComparisons(Array.isArray(sku?.data) ? sku.data : []);
        setPaymentMismatches([]);
      } catch (e) {
        console.error("Compare API error:", e);
      }
    };
    fetchData();
  }, [dateRange, platformFilter]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setUploadedFile(f);
    e.target.value = "";
  };

  const handleChooseFile = () => fileInputRef.current?.click();

  const handleUpload = async () => {
    if (!uploadedFile) return;
    const fd = new FormData();
    fd.append("rateCard", uploadedFile);
    try {
      await axios.post("/api/ratecard/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      const ov = await API.get("/overview");

      setOverview(ov?.data ?? null);
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && /\.(csv|xlsx|xls)$/i.test(f.name)) setUploadedFile(f);
  };

  const exportLedger = () => {
    const rows = paymentDisplay;
    const csv = ["ORDER ID,SKU,EXPECTED PAYOUT,RECEIVED PAYOUT,DIFFERENCE"].concat(
      rows.map((r) => `${r.orderId},${r.sku},${r.expected},${r.received},${r.difference}`)
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ledger_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const skuDisplay = skuComparisons.length > 0 ? skuComparisons : MOCK_SKU_LEDGER;
  const paymentDisplay = paymentMismatches.length > 0
    ? paymentMismatches.map((p) => ({
        orderId: p.orderId || p.sku,
        sku: p.sku || "-",
        expected: p.expectedPayout ?? 0,
        received: p.receivedPayout ?? 0,
        difference: (p.receivedPayout ?? 0) - (p.expectedPayout ?? 0),
      }))
    : MOCK_PAYMENT_LEDGER;

  const unresolvedCount = paymentDisplay.filter((r) => r.difference !== 0).length;

  const metrics = useMemo(() => {
    const ship = skuDisplay.filter((s) => (s.qtyShipped ?? s.internal?.shipped) !== (s.qtySold ?? s.platform?.total)).length;
    const paySum = paymentDisplay.filter((r) => r.difference !== 0).reduce((a, r) => a + Math.abs(r.difference), 0);
    const dead = skuDisplay.filter((s) => (s.qtyShipped ?? s.internal?.shipped ?? 0) === 0).length;
    const rateConflicts = MOCK_RATE_CARD.filter((r) => r.conflict).length;
    return {
      shippingMismatches: { value: ship || 12, sub: "-2%", type: "percent" },
      paymentMismatches: { value: paySum || 45230.5, sub: "Alert", type: "alert" },
      deadStockValue: { value: 112000, sub: "+1.4%", type: "percent" },
      rateCardConflicts: { value: rateConflicts || 8, sub: "High", type: "badge" },
    };
  }, [skuDisplay, paymentDisplay]);

  const formatMetric = (v, isAmount) =>
    isAmount ? `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : `${v || 0} Cases`;

  return (
    <div className="compare-page page-container">
      {/* Top bar */}
      <div className="compare-topbar">
        <div className="topbar-filters">
          <div className="filter-dropdown">
            <Calendar size={18} strokeWidth={2} />
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
          <div className="filter-dropdown">
            <LayoutGrid size={18} strokeWidth={2} />
            <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
              <option value="all">All Platforms (4)</option>
              {PLATFORMS.map((p) => (
                <option key={p} value={p.toLowerCase()}>{p}</option>
              ))}
            </select>
          </div>
          <div className="filter-dropdown">
            <Filter size={18} strokeWidth={2} />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="issues">Issues Only</option>
            </select>
          </div>
        </div>
        <button type="button" className="btn btn-export-ledger" onClick={exportLedger}>
          <Download size={18} strokeWidth={2} />
          <span>EXPORT LEDGER</span>
        </button>
      </div>

      {/* Metric cards */}
      <div className="compare-metrics">
        <div className="metric-card">
          <div className="metric-label">SHIPPING MISMATCHES</div>
          <div className="metric-row">
            <span className="metric-value">{formatMetric(metrics.shippingMismatches.value, false)}</span>
            <span className="metric-indicator metric-indicator--red">{metrics.shippingMismatches.sub}</span>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">PAYMENT MISMATCHES</div>
          <div className="metric-row">
            <span className="metric-value">{formatMetric(metrics.paymentMismatches.value, true)}</span>
            <span className="metric-indicator metric-indicator--orange">{metrics.paymentMismatches.sub}</span>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">DEAD STOCK VALUE</div>
          <div className="metric-row">
            <span className="metric-value">{formatMetric(metrics.deadStockValue.value, true)}</span>
            <span className="metric-indicator metric-indicator--green">{metrics.deadStockValue.sub}</span>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">RATE CARD CONFLICTS</div>
          <div className="metric-row">
            <span className="metric-value">{metrics.rateCardConflicts.value} Issues</span>
            <span className="metric-indicator metric-indicator--red">{metrics.rateCardConflicts.sub}</span>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="compare-charts">
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <h3 className="chart-title">SALES TREND</h3>
              <p className="chart-sub">Revenue Performance vs Last Month</p>
            </div>
          </div>
          <div className="chart-metric">
            <span className="chart-value">₹ 8,42,000</span>
            <span className="chart-growth">+12.5% Growth</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={MOCK_SALES_TREND}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis hide />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} fill="url(#salesGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <h3 className="chart-title">DAILY ORDERS</h3>
              <p className="chart-sub">Volume distribution per day</p>
            </div>
          </div>
          <div className="chart-metric">
            <span className="chart-value">1,240</span>
            <span className="chart-avg">Avg 41/day</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={MOCK_DAILY_ORDERS} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis hide />
              <Tooltip />
              <Bar dataKey="orders" fill="#93c5fd" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Master Rate Card Upload */}
      <div className="compare-card upload-section">
        <h3 className="section-title">
          <FileUp size={20} strokeWidth={2} />
          MASTER RATE CARD UPLOAD
        </h3>
        <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="file-input-hidden" />
        <div
          className={`upload-zone ${isDragging ? "upload-zone--active" : ""} ${uploadedFile ? "upload-zone--has-file" : ""}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <FileUp size={48} strokeWidth={1.5} className="upload-icon" />
          <p className="upload-text">
            Drag and drop your platform rate card (CSV/XLSX) or{" "}
            <button type="button" className="upload-browse" onClick={handleChooseFile}>browse</button>
          </p>
          <p className="upload-meta">MAX FILE SIZE: 10MB • SUPPORTED: FLIPKART, AMAZON, MEESHO</p>
          {uploadedFile && (
            <div className="upload-selected">
              <span>{uploadedFile.name}</span>
              <button type="button" className="btn btn-sm primary" onClick={handleUpload}>Upload</button>
            </div>
          )}
        </div>
      </div>

      {/* SKU Level Comparison Ledger */}
      <div className="compare-card ledger-section">
        <h3 className="section-title">SKU LEVEL COMPARISON LEDGER</h3>
        <div className="table-wrap">
          <table className="compare-table">
            <thead>
              <tr>
                <th>SKU ID / PRODUCT NAME</th>
                <th>PLATFORM</th>
                <th>QTY SOLD</th>
                <th>QTY SHIPPED</th>
                <th>STATUS</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {skuDisplay.length === 0 ? (
                <tr><td colSpan={6} className="empty-row">No SKU data — upload rate card or connect backend</td></tr>
              ) : (
                skuDisplay.map((row, idx) => {
                  const diff = row.diff ?? (Number(row.qtyShipped ?? row.internal?.shipped) - Number(row.qtySold ?? row.platform?.total));
                  const isShort = diff !== 0 && !isNaN(diff);
                  const statusType = row.statusType || (isShort ? "short" : "matched");
                  const statusText = row.status || (isShort ? `SHORT SHIPPED (${diff})` : "MATCHED");
                  return (
                    <tr key={row.skuId || row.sku || idx}>
                      <td>
                        <div className="cell-sku">{row.skuId || row.sku || "-"}</div>
                        <div className="cell-desc">{row.product || row.productName || "-"}</div>
                      </td>
                      <td>{typeof row.platform === "string" ? row.platform : (row.platform ? Object.keys(row.platform).join(", ") : "-")}</td>
                      <td>{row.qtySold ?? row.platform?.total ?? "-"}</td>
                      <td>{row.qtyShipped ?? row.internal?.shipped ?? "-"}</td>
                      <td>
                        <span className={`status-pill ${statusType}`}>{statusText}</span>
                      </td>
                      <td><button type="button" className="link-detail">Details</button></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Ledger + Rate Card Analysis */}
      <div className="compare-split">
        <div className="compare-card payment-section">
          <div className="payment-header">
            <h3 className="section-title">PAYMENT RECONCILIATION LEDGER</h3>
            {unresolvedCount > 0 && <span className="issues-badge">{unresolvedCount} UNRESOLVED ISSUES</span>}
          </div>
          <div className="table-wrap">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>ORDER ID</th>
                  <th>EXPECTED PAYOUT</th>
                  <th>RECEIVED PAYOUT</th>
                  <th>DIFFERENCE</th>
                </tr>
              </thead>
              <tbody>
                {paymentDisplay.map((row, idx) => (
                  <tr key={row.orderId || idx}>
                    <td>
                      <div className="cell-order">#{row.orderId}</div>
                      <div className="cell-sku-sub">{row.sku}</div>
                    </td>
                    <td>₹ {Number(row.expected || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td>₹ {Number(row.received || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className={row.difference === 0 ? "diff-zero" : "diff-negative"}>
                      {row.difference === 0 ? "₹ 0.00" : `- ₹ ${Math.abs(row.difference || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="compare-card rate-card-section">
          <h3 className="section-title">RATE CARD ANALYSIS</h3>
          {MOCK_RATE_CARD.map((r) => (
            <div key={r.platform} className="rate-card-item">
              <div className="rate-card-header">
                <span className="rate-card-label">{r.label}</span>
                {r.conflict ? <span className="rate-conflict">Conflict: {r.charged} charged</span> : <span className="rate-matched">Matched</span>}
              </div>
              <p className="rate-card-detail">Platform Rate: {r.rate}</p>
              <div className={`rate-bar ${r.conflict ? "rate-bar--conflict" : "rate-bar--matched"}`} style={{ width: r.conflict ? "90%" : "70%" }} />
            </div>
          ))}
          <div className="rate-card-item rate-card-profit">
            <span className="rate-card-label">ESTIMATED PROFIT/LOSS</span>
            <span className="rate-profit">₹ +1.28L</span>
          </div>
        </div>
      </div>

      {/* Recent Audit Activity */}
      <div className="compare-card audit-section">
        <h3 className="section-title">
          <Clock size={18} strokeWidth={2} />
          RECENT AUDIT ACTIVITY
        </h3>
        <ul className="audit-list">
          {MOCK_AUDIT.map((a, i) => (
            <li key={i} className="audit-item">
              <span className="audit-time">{a.time}</span>
              <span className="audit-text">
                {a.before}
                {a.link && <button type="button" className="audit-link">{a.link}</button>}
                {a.highlight && <span className="audit-highlight">{a.highlight}</span>}
                {a.after}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
